import { buildImgSrc } from '@/helpers/image-url'
import { useIntl } from '@/intl'
import { Card } from '@/scopes/common/components/Card'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics'
import { useAccoDetailsById } from '@/scopes/products/pages/components/DestinationSection/hooks'
import {
  Button,
  Icon,
  Image,
  LazyLoader,
  ScorePill,
  SpaceBetween,
  tk,
  Typography,
  verticalGap,
} from '@sunwebgroup/ui-components'
import { Rating } from '@sunwebgroup/ui-components/uds'
import { styled } from 'styled-components'
import { MapAccoCardLoading } from './MapAccoCardLoading'

export const AccoCardContainer = styled(Card)`
  position: absolute;
  bottom: ${tk('spacing_md')};
  left: ${tk('spacing_md')};
  right: ${tk('spacing_md')};
  display: flex;
  flex-direction: row;
  text-decoration: none;

  &:hover {
    color: inherit;
  }

  @media ${tk('mq_tablet')} {
    left: ${tk('spacing_lg')};
    bottom: ${tk('spacing_lg')};
    right: unset;
    width: 320px;
    flex-direction: column;
  }
`

const ImageContainer = styled.div`
  aspect-ratio: 1/1;
  width: 115px;
  flex-shrink: 0;

  @media ${tk('mq_tablet')} {
    width: 100%;
    aspect-ratio: 3/2;
  }
`

const StyledImage = styled(Image.Root)`
  object-fit: cover;
  height: 100%;
  width: 100%;
  display: block;
`

const LazyLoaderWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: grid;
`

export const CardMain = styled.div`
  padding: ${tk('spacing_md')};
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media ${tk('mq_tablet')} {
    padding: ${tk('spacing_lg')};
  }
`

export const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${tk('spacing_xl')};
  ${verticalGap('spacing_xs')}
`

const AccoName = styled(Typography)`
  font-size: ${tk('font_size_lg')};

  @media ${tk('mq_tablet')} {
    font-size: ${tk('font_size_xl')};
  }
`

const CardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`

const Location = styled(SpaceBetween)`
  color: ${tk('color_semantic_text_strong')};
`

const ReviewsScore = styled(SpaceBetween)`
  color: ${tk('color_semantic_text_strong')};
`

const BookNowButton = styled(Button)`
  &:hover {
    color: white;
  }
`

export function MapAccoCard({ accoId }: { accoId: string }) {
  const intl = useIntl()
  const noContentFallbackText = intl.formatMessage({ id: 'no_image' })
  const { sendEvent } = useAnalytics()

  const { accoDetails, isLoading } = useAccoDetailsById(accoId)

  if (isLoading) {
    return <MapAccoCardLoading />
  }

  if (!accoDetails) {
    return null
  }

  const handleBookNowClick = () => {
    sendEvent(
      createPDPClickEvent({
        element: 'Button',
        description: 'Clicked on book now button in map card',
        result: `Map card for ${accoDetails.name} is clicked`,
      }),
    )
  }

  return (
    <AccoCardContainer>
      <ImageContainer>
        <LazyLoader>
          {({ inView }) => (
            <LazyLoaderWrapper>
              <StyledImage
                defaultSource={{ src: buildImgSrc(accoDetails.imageUrl, 320, 216), width: 320, height: 216 }}
                loaderSource={{ src: buildImgSrc(accoDetails.imageUrl, 20) }}
                sources={[]}
                alternativeText={accoDetails.name}
                startImmediateLoad={inView}
              >
                <Image.Error>
                  <Icon glyph="IconNoImage" size="lg" />
                  <Typography $variant="body-lg-bold">{noContentFallbackText}</Typography>
                </Image.Error>
              </StyledImage>
            </LazyLoaderWrapper>
          )}
        </LazyLoader>
      </ImageContainer>

      <CardMain>
        <CardHeader>
          <SpaceBetween direction="x" alignItems="center" spacing={tk('spacing_xs')} justifyContent="space-between">
            <Rating rating={accoDetails.rating} />
            {accoDetails.userRating > 0 && (
              <ReviewsScore direction="x" alignItems="center" spacing={tk('spacing_xs')}>
                <ScorePill>{accoDetails.userRating}</ScorePill>
              </ReviewsScore>
            )}
          </SpaceBetween>

          <AccoName $variant="heading-md">{accoDetails.name}</AccoName>

          <Location direction="x" alignItems="center" spacing={tk('spacing_2xs')}>
            <Typography $variant="body-xs">
              {accoDetails.city}
              ,
              {accoDetails.region}
              ,
              {accoDetails.country}
            </Typography>
          </Location>
        </CardHeader>

        <CardFooter>
          <BookNowButton as="a" href={accoDetails.url} onClick={handleBookNowClick}>
            {intl.formatMessage({ id: 'destination_map_detail_card_cta' })}
          </BookNowButton>
        </CardFooter>
      </CardMain>
    </AccoCardContainer>
  )
}

MapAccoCard.displayName = 'MapAccoCard'
